import { useId, useRef, useState, type ChangeEvent, type DragEvent, type KeyboardEvent } from "react";
import { ACCEPTED_MEDIA_TYPES } from "../../core/media/validateMediaFile";

type MediaDropzoneProps = {
  disabled?: boolean;
  onFile: (file: File) => void;
};

export function MediaDropzone({ disabled = false, onFile }: MediaDropzoneProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const submit = (file?: File) => {
    if (!file || disabled) return;
    onFile(file);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    submit(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    submit(event.dataTransfer.files?.[0]);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    inputRef.current?.click();
  };

  return (
    <div
      className={`media-dropzone${dragActive ? " is-dragging" : ""}${disabled ? " is-disabled" : ""}`}
      onClick={() => !disabled && inputRef.current?.click()}
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled) setDragActive(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={() => setDragActive(false)}
      onDrop={handleDrop}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-describedby={`${inputId}-hint`}
    >
      <input
        ref={inputRef}
        id={inputId}
        className="media-dropzone__input"
        type="file"
        accept={ACCEPTED_MEDIA_TYPES}
        onChange={handleChange}
        disabled={disabled}
      />
      <span className="media-dropzone__eyebrow">IMPORT MEDIA</span>
      <strong>{disabled ? "Reading media…" : "Drop image or video"}</strong>
      <small id={`${inputId}-hint`}>PNG · JPEG · WebP · GIF · SVG · MP4 · WebM · MOV*</small>
      <small>*MOV depends on the codecs available in your browser.</small>
    </div>
  );
}
